import React, { createContext, useContext, useEffect, useRef, useCallback, useState, ReactNode } from "react";
import { io, Socket } from "socket.io-client";
import { useUser } from "./UserContext";

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  connectionFailed: boolean;
  subscribe: (event: string, handler: (...args: any[]) => void) => () => void;
  emit: (event: string, payload?: any) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);

  // Registry of active subscriptions so they can be re-applied when socket changes
  const listenersRef = useRef<Map<string, Set<(...args: any[]) => void>>>(new Map());

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    if (!socketRef.current) {
      const socketUrl = import.meta.env.VITE_API_URL || "/";

      socketRef.current = io(socketUrl, {
        path: "/ws",
        withCredentials: true,
        autoConnect: false,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        reconnectionAttempts: 10,
        transports: ["websocket", "polling"],
      });
    }

    const socket = socketRef.current;

    const handleConnect = () => {
      setConnected(true);
      setConnectionFailed(false);
    };

    const handleDisconnect = () => {
      setConnected(false);
    };

    const handleReconnectFailed = () => {
      setConnectionFailed(true);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.io.on("reconnect_failed", handleReconnectFailed);

    // Re-apply all registered listeners to the new/current socket
    listenersRef.current.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        socket.on(event, handler);
      });
    });

    socket.connect();

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.io.off("reconnect_failed", handleReconnectFailed);
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [user]);

  const subscribe = useCallback((event: string, handler: (...args: any[]) => void): (() => void) => {
    // Register in the listener map
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event)!.add(handler);

    // Attach to current socket if it exists
    const socket = socketRef.current;
    if (socket) {
      socket.on(event, handler);
    }

    // Cleanup: remove from both registry and socket
    return () => {
      const handlers = listenersRef.current.get(event);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          listenersRef.current.delete(event);
        }
      }
      if (socket) {
        socket.off(event, handler);
      }
    };
  }, []);

  const emit = useCallback((event: string, payload?: any) => {
    const socket = socketRef.current;
    if (socket && socket.connected) {
      socket.emit(event, payload);
    }
  }, []);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, connected, connectionFailed, subscribe, emit }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error("useSocketContext must be used within SocketProvider");
  return ctx;
};
